import { useEffect, useRef, useState } from 'react';
import {
  listMessages, sendMessage, listRecords, createRecord, updateRecord,
  recommendCadence, ApiError,
} from '../api.js';
import type { Message, CompanyDetail } from '../api.js';
import { ago } from '../ui.js';
import { STAGES } from '../data.js';

// ─────────────────────────────────────────────────────────────────────────────
// THE CONSOLE — one input, four intents, each with somewhere to start.
//
// V1 gave each mode a placeholder and a button. Reviewing it against what a rep
// actually does, two faults:
//
//   1. AN EMPTY BOX IS A BLANK-PAGE PROBLEM. "Ask anything about this account"
//      is not an invitation, it is a demand that you already know what to ask.
//      Every mode now offers two to four concrete openings — Chat fills the box
//      so you can edit before sending; Plan and Manage run directly.
//
//   2. PLAN WAS TOO NARROW. I had built it as "draft a cadence," which is one
//      artefact of planning, not planning. Kyle caught it: the play on an
//      account is the cadence AND who you enter through AND what you are
//      selling AND what happens next. A mode called Plan that only makes
//      touches teaches you it is the sequence button, and then you stop
//      looking for the rest.
//
// The modes map to the four things a rep does at a record, in the order they
// tend to do them:
//
//   Chat    work out what is true here
//   Plan    decide the play — cadence, entry, deal, next action
//   Email   say it
//   Manage  record what happened and where it now stands
// ─────────────────────────────────────────────────────────────────────────────

export type Mode = 'chat' | 'plan' | 'email' | 'manage';

const KINDS = ['note', 'call', 'meeting', 'linkedin'] as const;

export function Console({ c, onCompose, onChanged, onOpenFold }: {
  c: CompanyDetail;
  onCompose: () => void;
  onChanged: () => void;
  onOpenFold: (id: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('chat');
  const [text, setText] = useState('');
  const [kind, setKind] = useState<string>('note');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Message[]>([]);
  const [showThread, setShowThread] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (mode !== 'chat' || msgs.length) return;
    listMessages(c.slug).then((r) => setMsgs(r.messages)).catch(() => {});
  }, [mode, c.slug, msgs.length]);

  const say = (m: string) => { setMsg(m); setError(null); };
  const fill = (t: string) => { setText(t); box.current?.focus(); };

  async function go<T>(key: string, fn: () => Promise<T>, after?: (r: T) => void) {
    setBusy(key); setError(null); setMsg(null);
    try { after?.(await fn()); } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  // --- the four intents -----------------------------------------------------

  async function ask() {
    if (!text.trim()) return;
    await go('ask', () => sendMessage(c.slug, text.trim()), (r) => {
      setMsgs(r.messages); setText(''); setShowThread(true);
    });
  }

  async function draftCadence() {
    await go('cadence', async () => {
      // V1 created a sequence on every press, stacking a new empty one each
      // time. Reuse the draft that is already there — planning twice is normal.
      const existing = await listRecords<{ id: string; status: string }>(
        'sequences', { company_id: c.id });
      const draft = existing.records.find((s) => s.status === 'draft');
      const id = draft?.id ?? (await createRecord<{ id: string }>('sequences', {
        company_id: c.id, mode: 'A',
        activation_rule: 'Any real dialogue pauses every remaining cold touch on the whole account.',
      })).record.id;
      return recommendCadence(id);
    }, () => {
      say('Cadence drafted. Nothing is armed — open it and rewrite each touch in your voice.');
      onChanged(); onOpenFold('sequence');
    });
  }

  async function logIt() {
    if (!text.trim()) return;
    const [first, ...rest] = text.trim().split('\n');
    await go('log', () => createRecord('activities', {
      company_id: c.id, kind, category: 'sales',
      summary: first.slice(0, 200), detail: rest.join('\n') || null,
      occurred_at: new Date().toISOString(),
    }), () => {
      setText(''); say(`Logged as a ${kind}.`); onChanged(); onOpenFold('stream');
    });
  }

  async function setNextAction() {
    if (!text.trim()) return;
    await go('next', () => updateRecord('companies', c.id, {
      next_action: text.trim().slice(0, 200),
      next_action_date: new Date(Date.now() + 3 * 86_400_000).toISOString().slice(0, 10),
    }), () => { setText(''); say('Next action set for three days out.'); onChanged(); });
  }

  const withEmail = c.contacts.filter((x) => x.email);
  const unwritten = c.touches.filter((t) => !t.body).length;

  return (
    <div className={`console console-${mode}`}>
      <div className="console-modes" role="tablist" aria-label="What do you want to do">
        {(['chat', 'plan', 'email', 'manage'] as Mode[]).map((m) => (
          <button key={m} role="tab" aria-selected={mode === m}
            className={mode === m ? 'on' : ''}
            onClick={() => { setMode(m); setMsg(null); setError(null); }}>
            {m}
          </button>
        ))}
        {mode === 'chat' && msgs.length > 0 && (
          <button className="console-history" onClick={() => setShowThread(!showThread)}>
            {showThread ? 'Hide' : `${msgs.length} messages`}
          </button>
        )}
      </div>

      {showThread && mode === 'chat' && (
        <div className="console-thread">
          {msgs.slice(-6).map((m) => (
            <div key={m.id} className={`cmsg ${m.role}`}>
              <p>{m.content}</p>
              <span className="meta">{ago(m.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      {/* SOMEWHERE TO START. The chips are the answer to the blank page — and in
          Plan and Manage they are the feature, not a shortcut to it. */}
      <div className="console-starts">
        {mode === 'chat' && (
          <>
            <button onClick={() => fill('What is the wedge here, and what evidence do we have for it?')}>
              What is the wedge?
            </button>
            <button onClick={() => fill('Who should I open with, and why that person over the others?')}>
              Who do I open with?
            </button>
            <button onClick={() => fill('What is blocking this account and what would unblock it?')}>
              What is blocking this?
            </button>
            <button onClick={() => fill('What do we still not know that would change how I approach this?')}>
              What do we not know?
            </button>
          </>
        )}

        {mode === 'plan' && (
          <>
            <button className="go" disabled={busy === 'cadence' || c.contacts.length === 0}
              title={c.contacts.length ? '' : 'Needs someone to aim at'}
              onClick={() => void draftCadence()}>
              {busy === 'cadence' ? 'Drafting…' : 'Draft the cadence'}
            </button>
            <button onClick={() => onOpenFold('ways-in')}>Choose who to enter through</button>
            <button onClick={() => onOpenFold('deals')}>Open a deal</button>
            <button onClick={() => fill('')}>Set the next action</button>
          </>
        )}

        {mode === 'email' && (
          <>
            <button className="go" onClick={onCompose} disabled={c.contacts.length === 0}>
              Write a new email
            </button>
            {unwritten > 0 && (
              <button onClick={() => onOpenFold('sequence')}>
                Write {unwritten} unwritten touch{unwritten > 1 ? 'es' : ''}
              </button>
            )}
            <button onClick={() => onOpenFold('stream')}>See the thread</button>
          </>
        )}

        {mode === 'manage' && (
          <>
            {KINDS.map((k) => (
              <button key={k} className={kind === k ? 'on' : ''} onClick={() => {
                setKind(k); box.current?.focus();
              }}>{k}</button>
            ))}
            <select className="console-stage" value={c.stage}
              onChange={(e) => void go('stage',
                () => updateRecord('companies', c.id, { stage: e.target.value }),
                () => { say(`Stage moved to ${e.target.value.replace('_', ' ')}.`); onChanged(); })}>
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </>
        )}
      </div>

      <textarea
        ref={box}
        rows={text.split('\n').length > 2 ? 5 : 2}
        value={text}
        placeholder={
          mode === 'chat' ? 'Ask anything about this account, or pick a question above.'
          : mode === 'plan' ? 'Type the next action, or use a button above.'
          : mode === 'email' ? 'The composer opens with everyone on this account.'
          : `What happened? Logging as a ${kind}.`
        }
        disabled={mode === 'email'}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && mode !== 'email') {
            e.preventDefault();
            if (mode === 'chat') void ask();
            else if (mode === 'manage') void logIt();
            else void setNextAction();
          }
        }}
      />

      <div className="console-foot">
        {mode !== 'email' && (
          <button className="primary" disabled={!!busy || !text.trim()}
            onClick={() => {
              if (mode === 'chat') void ask();
              else if (mode === 'manage') void logIt();
              else void setNextAction();
            }}>
            {busy ? 'Working…'
              : mode === 'chat' ? 'Ask'
              : mode === 'manage' ? `Log ${kind}`
              : 'Set next action'}
          </button>
        )}
        <span className="meta">
          {mode === 'chat' && 'Reads the research, the committee, and every settled decision.'}
          {mode === 'plan' && 'The play on this account: who you enter through, what you send, what you are selling.'}
          {mode === 'email' && `${withEmail.length} of ${c.contacts.length} on this account have an address.`}
          {mode === 'manage' && 'Lands in the stream below, in order.'}
        </span>
      </div>

      {msg && <p className="ok-note">{msg}</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
